import { useState, useEffect, useRef, lazy, Suspense } from "react";
import "@/App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Capacitor } from '@capacitor/core';
import { AuthProvider, useAuth } from "./context/AuthContext";
import { FeatureFlagProvider, useFeatureFlags } from "./context/FeatureFlagContext";
import { Toaster } from "./components/ui/sonner";
import InstallPrompt from "./components/pwa/InstallPrompt";
import { LogOut, Loader2 } from "lucide-react";
import { DutchDateIcon } from "./components/brand/DutchDateLogo";
import { useAndroidBackButton } from "./hooks/useAndroidBackButton";
import BottomNav from "./components/BottomNav";
import PageTransitionWrapper from "./components/PageTransitionWrapper";
import ErrorBoundary from "./components/ErrorBoundary";
import PremiumExpirationBanner from "./components/PremiumExpirationBanner";
import GdprConsentModal from "./components/GdprConsentModal";
import PolicyConsentModal from "./components/PolicyConsentModal";
import AIConsentModal from "./components/AIConsentModal";
import CookieBanner from "./components/CookieBanner";
import StepUpModal from "./components/StepUpModal";
import PostDateFeedbackHandler from "./components/PostDateFeedbackHandler";
import { DemoWalkthroughButton } from "./components/DemoWalkthrough";
import DeviceFrame from "./components/DeviceFrame";
import api from "./lib/api";
import { usePushNotifications } from "./hooks/usePushNotifications";
import { initPwaTracking } from "./lib/pwa/track";

// Lazy-loaded page components (route-level code splitting)
const AuthPage = lazy(() => import("./pages/AuthPage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const OnboardingWizard = lazy(() => import("./pages/OnboardingWizard"));
const FinancialProfilePage = lazy(() => import("./pages/FinancialProfilePage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const MatchesPage = lazy(() => import("./pages/MatchesPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const DatePlanPage = lazy(() => import("./pages/DatePlanPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const PremiumPage = lazy(() => import("./pages/PremiumPage"));
const PremiumSuccessPage = lazy(() => import("./pages/PremiumSuccessPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const SafetyPage = lazy(() => import("./pages/SafetyPage"));
const ReferralPage = lazy(() => import("./pages/ReferralPage"));
const OnboardingChat = lazy(() => import("./pages/OnboardingChat"));
const HelpCenterPage = lazy(() => import("./pages/HelpCenterPage"));
const BookingsPage = lazy(() => import("./pages/BookingsPage"));
const AchievementsPage = lazy(() => import("./pages/AchievementsPage"));
const ForumPage = lazy(() => import("./pages/ForumPage"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const InsightsPage = lazy(() => import("./pages/InsightsPage"));
const QuizPage = lazy(() => import("./pages/QuizPage"));
const DateRecapPage = lazy(() => import("./pages/DateRecapPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const EulaPage = lazy(() => import("./pages/EulaPage"));
const PolicyPage = lazy(() => import("./pages/PolicyPage"));
const HelpLegalPage = lazy(() => import("./pages/HelpLegalPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const MissionPage = lazy(() => import("./pages/MissionPage"));
const VisionPage = lazy(() => import("./pages/VisionPage"));
const SafetyPublicPage = lazy(() => import("./pages/SafetyPublicPage"));
const ChildSafetyPage = lazy(() => import("./pages/ChildSafetyPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const PricingSuccessPage = lazy(() => import("./pages/PricingSuccessPage"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const KnowledgeBasePage = lazy(() => import("./pages/KnowledgeBasePage"));
const ChatsPage = lazy(() => import("./pages/ChatsPage"));
const AnalyticsPage = lazy(() => import("./pages/admin/AnalyticsPage"));
const ElitePage = lazy(() => import("./pages/ElitePage"));
const EliteEventsPage = lazy(() => import("./pages/EliteEventsPage"));
const EliteAnalyticsPage = lazy(() => import("./pages/EliteAnalyticsPage"));
const AIToolsPage = lazy(() => import("./pages/AIToolsPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));

// Lazy-loaded named exports need wrapper
const ArticleList = lazy(() => import("./pages/BlogPage").then(m => ({ default: m.ArticleList })));
const ArticleDetail = lazy(() => import("./pages/BlogPage").then(m => ({ default: m.ArticleDetail })));

const PageLoader = () => (
  <div className="flex items-center justify-center h-full min-h-[200px]">
    <Loader2 className="w-6 h-6 animate-spin text-dutch-teal" />
  </div>
);

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-white">
      <div className="flex flex-col items-center gap-4">
        <DutchDateIcon size={64} className="animate-pulse" />
        <p className="text-sm text-muted-foreground font-body">Loading...</p>
      </div>
    </div>
  );
  return isAuthenticated ? children : <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname)}`} />;
};

const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [aiConsentOpen, setAiConsentOpen] = useState(false);
  const handleLogout = () => {
    logout();
    nav(Capacitor.isNativePlatform() ? '/auth' : '/');
  };
  // Open the AI consent modal whenever any /api/ai/* call returns 403
  // with `code=AI_CONSENT_REQUIRED` (Apple App Review 5.1.1 / 5.1.2).
  useEffect(() => {
    const handler = () => setAiConsentOpen(true);
    window.addEventListener('ai:consent-required', handler);
    return () => window.removeEventListener('ai:consent-required', handler);
  }, []);
  // Onboarding gate: new/returning users who haven't finished the setup wizard
  // are routed into it before they can reach the main app. Admins are exempt.
  if (user && user.role !== 'admin' && !user.onboarding_complete) {
    return <Navigate to="/onboarding" replace />;
  }
  return (
    <div className="min-h-full bg-white relative padding-safe-top">
      <GdprConsentModal />
      <PolicyConsentModal />
      <AIConsentModal open={aiConsentOpen} onClose={() => setAiConsentOpen(false)} />
      {/* Persistent top-right logout */}
      <div className="absolute top-2 right-2 z-30" style={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}>
        <button
          onClick={handleLogout}
          data-testid="global-logout-btn"
          className="p-1.5 rounded-full bg-neutral-100/80 hover:bg-red-50 text-neutral-400 hover:text-red-500 transition-colors"
          title="Sign out"
        >
          <LogOut size={14} />
        </button>
      </div>
      <PremiumExpirationBanner />
      <div className="pb-24">
        <PageTransitionWrapper>{children}</PageTransitionWrapper>
      </div>
      <BottomNav />
      <DemoWalkthroughButton />
    </div>
  );
};

// Google OAuth Callback - processes session_id from URL fragment
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      navigate('/auth');
      return;
    }

    const exchangeSession = async () => {
      try {
        const res = await api.post(`/auth/google/session?session_id=${sessionId}`);
        const { token, user } = res.data;
        localStorage.setItem('dutchdate_token', token);
        navigate('/dashboard', { replace: true });
      } catch {
        navigate('/auth');
      }
    };
    exchangeSession();
  }, [navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-white">
      <div className="flex flex-col items-center gap-4">
        <DutchDateIcon size={64} className="animate-pulse" />
        <p className="text-sm text-muted-foreground font-body">Signing in with Google...</p>
      </div>
    </div>
  );
};

const AuthRedirect = () => {
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const { user } = useAuth();
  // Admins land on /admin by default; everyone else on /discover.
  // An explicit ?redirect=... param always wins.
  const fallback = user?.role === 'admin' ? '/admin' : '/matches';
  return <Navigate to={redirect || fallback} />;
};

// Homepage lives at the bare domain "/" (the canonical URL). Native apps
// jump straight to auth; signed-in users go to their in-app home.
const HomeRoute = () => {
  const { isAuthenticated, user } = useAuth();
  if (isAuthenticated) return <Navigate to={user?.role === 'admin' ? '/admin' : '/matches'} replace />;
  if (Capacitor.isNativePlatform()) return <Navigate to="/auth" replace />;
  return <LandingPage />;
};

// Legacy /landing URL now consolidates into "/" (preserves the query string
// so QR / deep links such as ?join=1 keep working).
const LandingRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/${location.search}`} replace />;
};

const AppRouter = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const FEATURE_FLAGS = useFeatureFlags();

  // Initialize native push notifications when authenticated
  const authToken = isAuthenticated ? localStorage.getItem('dutchdate_token') : null;
  usePushNotifications(authToken);

  // Global Android/iOS hardware-back-button handler (single listener for the whole app)
  useAndroidBackButton();

  // CRITICAL: Detect session_id synchronously before any route renders
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
    <CookieBanner />
    <Routes>
      <Route path="/landing" element={<LandingRedirect />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/eula" element={<EulaPage />} />
      <Route path="/acceptableuse" element={<PolicyPage docType="acceptableuse" title="Acceptable Use & Safety Policy" seoDescription="DutchDate's Acceptable Use & Safety Policy: community rules, safety tips, reporting and blocking." path="/acceptableuse" />} />
      <Route path="/lawenforcement" element={<PolicyPage docType="lawenforcement" title="Law Enforcement Request Guidelines" seoDescription="How law enforcement can request user information from DutchDate (Dutch Date LLC)." path="/lawenforcement" />} />
      <Route path="/refunds" element={<PolicyPage docType="refunds" title="Refund & Cancellation Policy" seoDescription="DutchDate subscription cancellation and refund policy." path="/refunds" />} />
      <Route path="/pricing-disclosures" element={<PolicyPage docType="pricingdisclosures" title="Pricing & Payment Disclosures" seoDescription="What you pay for on DutchDate: subscription pricing, billing, renewal and cancellation disclosures." path="/pricing-disclosures" />} />
      <Route path="/support" element={<PolicyPage docType="support" title="Support & Contact" seoDescription="Contact DutchDate support at hello@dutchdate.app." path="/support" />} />
      <Route path="/help" element={<HelpLegalPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/mission" element={<MissionPage />} />
      <Route path="/vision" element={<VisionPage />} />
      <Route path="/safety-info" element={<SafetyPublicPage />} />
      <Route path="/child-safety" element={<ChildSafetyPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/pricing/success" element={<PricingSuccessPage />} />
      {FEATURE_FLAGS.marketingBlog && <Route path="/blog" element={<BlogListPage />} />}
      {FEATURE_FLAGS.marketingBlog && <Route path="/blog/:slug" element={<BlogArticlePage />} />}
      <Route path="/auth" element={isAuthenticated ? <AuthRedirect /> : <AuthPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />
      <Route path="/onboarding-chat" element={<ProtectedRoute><OnboardingChatWrapper /></ProtectedRoute>} />
      <Route path="/" element={<HomeRoute />} />
      <Route path="/discover" element={<ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/matches" element={<ProtectedRoute><AppLayout><MatchesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/chats" element={<ProtectedRoute><AppLayout><ChatsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/chat/:matchId" element={<ProtectedRoute><AppLayout><ChatPage /></AppLayout></ProtectedRoute>} />
      <Route path="/date-plan" element={<ProtectedRoute><AppLayout><DatePlanPage /></AppLayout></ProtectedRoute>} />
      {FEATURE_FLAGS.eventsCalendar && <Route path="/events" element={<ProtectedRoute><AppLayout><EventsPage /></AppLayout></ProtectedRoute>} />}
      <Route path="/profile" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />
      <Route path="/financial-profile" element={<ProtectedRoute><AppLayout><FinancialProfilePage /></AppLayout></ProtectedRoute>} />
      <Route path="/premium" element={<ProtectedRoute><AppLayout><PremiumPage /></AppLayout></ProtectedRoute>} />
      <Route path="/premium/success" element={<ProtectedRoute><AppLayout><PremiumSuccessPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/admin/knowledge-base" element={<ProtectedRoute><KnowledgeBasePage /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/safety" element={<ProtectedRoute><SafetyPage /></ProtectedRoute>} />
      {FEATURE_FLAGS.blog && <Route path="/blog" element={<ProtectedRoute><AppLayout><ArticleList /></AppLayout></ProtectedRoute>} />}
      {FEATURE_FLAGS.blog && <Route path="/blog/:slug" element={<ProtectedRoute><AppLayout><ArticleDetail /></AppLayout></ProtectedRoute>} />}
      {FEATURE_FLAGS.referralProgram && <Route path="/referral" element={<ProtectedRoute><AppLayout><ReferralPage /></AppLayout></ProtectedRoute>} />}
      <Route path="/help" element={<ProtectedRoute><AppLayout><HelpCenterPage /></AppLayout></ProtectedRoute>} />
      <Route path="/bookings" element={<ProtectedRoute><AppLayout><BookingsPage /></AppLayout></ProtectedRoute>} />
      {FEATURE_FLAGS.achievements && <Route path="/achievements" element={<ProtectedRoute><AppLayout><AchievementsPage /></AppLayout></ProtectedRoute>} />}
      {FEATURE_FLAGS.forum && <Route path="/forum" element={<ProtectedRoute><AppLayout><ForumPage /></AppLayout></ProtectedRoute>} />}
      {FEATURE_FLAGS.calendarAvailability && <Route path="/calendar" element={<ProtectedRoute><AppLayout><CalendarPage /></AppLayout></ProtectedRoute>} />}
      <Route path="/settings" element={<ProtectedRoute><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><AppLayout><SubscriptionPage /></AppLayout></ProtectedRoute>} />
      {FEATURE_FLAGS.profileInsights && <Route path="/insights" element={<ProtectedRoute><AppLayout><InsightsPage /></AppLayout></ProtectedRoute>} />}
      {FEATURE_FLAGS.compatibilityQuiz && <Route path="/quiz" element={<ProtectedRoute><AppLayout><QuizPage /></AppLayout></ProtectedRoute>} />}
      {FEATURE_FLAGS.dateRecap && <Route path="/date-recaps" element={<ProtectedRoute><AppLayout><DateRecapPage /></AppLayout></ProtectedRoute>} />}
      {FEATURE_FLAGS.premiumElite && <Route path="/elite" element={<ProtectedRoute><AppLayout><ElitePage /></AppLayout></ProtectedRoute>} />}
      {FEATURE_FLAGS.premiumElite && <Route path="/elite/events" element={<ProtectedRoute><AppLayout><EliteEventsPage /></AppLayout></ProtectedRoute>} />}
      {FEATURE_FLAGS.premiumElite && <Route path="/elite/analytics" element={<ProtectedRoute><AppLayout><EliteAnalyticsPage /></AppLayout></ProtectedRoute>} />}
      {FEATURE_FLAGS.advancedAI && <Route path="/elite/ai-tools" element={<ProtectedRoute><AppLayout><AIToolsPage /></AppLayout></ProtectedRoute>} />}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </Suspense>
  );
};

const OnboardingChatWrapper = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = searchParams.get('edit') === 'true';
  const { refreshUser } = useAuth();
  const handleComplete = async () => {
    try { await refreshUser(); } catch { /* swallow — user still navigates */ }
    const target = isEdit ? '/profile' : '/dashboard';
    if (Capacitor.isNativePlatform()) {
      window.location.href = target;
    } else {
      navigate(target);
    }
  };
  return <OnboardingChat isProfileEdit={isEdit} onComplete={handleComplete} />;
};

function App() {
  // PWA install-funnel: record page_load / installed once on app start.
  useEffect(() => { initPwaTracking(); }, []);

  // On native platforms, configure the status bar so the OS chrome (time, 5G, battery)
  // is visible at the top of the screen instead of being covered by the webview.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    (async () => {
      try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        // Don't draw the webview behind the status bar
        await StatusBar.setOverlaysWebView({ overlay: false });
        // Dark icons on a light status-bar background (matches our light teal/white theme)
        await StatusBar.setStyle({ style: Style.Light });
        await StatusBar.setBackgroundColor({ color: '#ffffff' });
        await StatusBar.show();
      } catch (e) {
        // StatusBar plugin not available — non-fatal
        console.warn('StatusBar init skipped:', e);
      }
    })();
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <FeatureFlagProvider>
            <DeviceFrame>
              <AppRouter />
              <Toaster position="top-center" richColors />
              <StepUpModal />
              <PostDateFeedbackHandler />
              <InstallPrompt />
            </DeviceFrame>
          </FeatureFlagProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
